import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Sparkles, Heart, History, Dumbbell, AudioWaveform } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import SongCard from "@/components/SongCard";
import { fmtTime } from "@/lib/music";

export default function Home() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    const { data } = await api.get("/home");
    setData(data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const favorite = async (song) => {
    await api.post(`/songs/${song.id}/favorite`);
    load();
  };
  const del = async (song) => {
    if (!window.confirm(`Delete "${song.title}"?`)) return;
    await api.delete(`/songs/${song.id}`);
    load();
  };

  return (
    <div className="space-y-8 sw-fade-up">
      <header>
        <p className="font-mono-sw text-xs uppercase tracking-[0.2em] text-cyan-400">Welcome back</p>
        <h1 className="font-display text-3xl font-extrabold tracking-tight text-white sm:text-4xl">
          Hi, {user?.name?.split(" ")[0] || "there"}
        </h1>
        <p className="mt-1 text-sm text-slate-400">Listen. Separate. Understand. Learn.</p>
      </header>

      {/* Analyze CTA */}
      <button
        data-testid="analyze-song-btn"
        onClick={() => nav("/analyze")}
        className="group relative flex w-full items-center gap-4 overflow-hidden rounded-2xl border border-cyan-500/30 bg-gradient-to-r from-[#131822] to-[#0B0E14] p-5 text-left transition-all hover:border-cyan-400/60"
      >
        <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-cyan-500/15 text-cyan-400">
          <Plus size={26} />
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2 font-display text-lg font-bold text-white">
            Analyze a Song <Sparkles size={16} className="text-amber-400" />
          </div>
          <p className="text-sm text-slate-400">Upload audio and unlock stems, chords, lyrics & more</p>
        </div>
        <AudioWaveform className="text-cyan-500/40 transition-transform group-hover:scale-110" size={40} />
      </button>

      {/* Continue practice */}
      {data?.continue_practice?.length > 0 && (
        <section>
          <SectionTitle icon={Dumbbell} title="Continue Practice" />
          <div className="flex gap-3 overflow-x-auto pb-2">
            {data.continue_practice.map((s) => (
              <button
                key={s.id}
                data-testid={`continue-practice-${s.id}`}
                onClick={() => nav(`/studio/${s.song_id}?tab=practice`)}
                className="w-56 shrink-0 rounded-xl border border-[#222A38] bg-[#131822] p-4 text-left transition-colors hover:border-[#3A4659]"
              >
                <div className="truncate font-semibold text-white">{s.song_title}</div>
                <div className="mt-1 text-xs text-slate-400">{s.part} · {s.section}</div>
                <div className="mt-2 flex items-center justify-between font-mono-sw text-[11px] text-cyan-400">
                  <span>{s.speed}% speed</span>
                  <span>{fmtTime(s.duration_seconds)}</span>
                </div>
              </button>
            ))}
          </div>
        </section>
      )}

      {/* Recent */}
      <section>
        <SectionTitle icon={History} title="Recently Analyzed" />
        {data?.recent?.length ? (
          <div className="grid gap-3">
            {data.recent.map((s) => (
              <SongCard key={s.id} song={s} onFavorite={favorite} onDelete={del} />
            ))}
          </div>
        ) : (
          <EmptyState text="No songs yet. Analyze your first song to get started." />
        )}
      </section>

      {/* Favorites */}
      {data?.favorites?.length > 0 && (
        <section>
          <SectionTitle icon={Heart} title="Favorites" />
          <div className="grid gap-3">
            {data.favorites.map((s) => (
              <SongCard key={s.id} song={s} onFavorite={favorite} onDelete={del} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

function SectionTitle({ icon: Icon, title }) {
  return (
    <div className="mb-3 flex items-center gap-2">
      <Icon size={16} className="text-cyan-400" />
      <h2 className="font-display text-lg font-bold text-white">{title}</h2>
    </div>
  );
}

function EmptyState({ text }) {
  return (
    <div className="rounded-xl border border-dashed border-[#222A38] bg-[#131822]/50 p-8 text-center text-sm text-slate-500">
      {text}
    </div>
  );
}
